import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
  Alert,
  Tabs,
  Tab,
  alpha,
  Fade,
  Slide
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  Upload as UploadIcon,
  PhotoCamera as PhotoCameraIcon,
  FlipCameraAndroid as FlipCameraIcon,
  DocumentScanner as ScannerIcon,
  CameraEnhance as CameraEnhanceIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Client-side image preprocessing for better OCR accuracy
 * Uses Canvas 2D API — no external dependencies
 */
async function preprocessImage(sourceCanvas) {
  const width = Math.min(sourceCanvas.width, 1280);
  const scale = width / sourceCanvas.width;
  const height = Math.round(sourceCanvas.height * scale);
  
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  // Draw scaled image
  ctx.drawImage(sourceCanvas, 0, 0, width, height);
  
  // Get image data for pixel manipulation
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  // Step 1: Convert to grayscale
  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    data[i] = gray;
    data[i + 1] = gray;
    data[i + 2] = gray;
  }
  
  // Step 2: Auto-contrast (stretch histogram)
  let min = 255, max = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i] < min) min = data[i];
    if (data[i] > max) max = data[i];
  }
  const range = max - min || 1;
  for (let i = 0; i < data.length; i += 4) {
    const val = Math.round(((data[i] - min) / range) * 255);
    data[i] = val;
    data[i + 1] = val;
    data[i + 2] = val;
  }
  
  // Step 3: Slight sharpening (unsharp mask via convolution)
  ctx.putImageData(imageData, 0, 0);
  
  // Apply a subtle sharpen by overlaying a blurred negative
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = width;
  tempCanvas.height = height;
  const tempCtx = tempCanvas.getContext('2d');
  tempCtx.filter = 'blur(1px)';
  tempCtx.drawImage(canvas, 0, 0);
  
  // Blend: original + (original - blurred) * strength
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  // Re-draw original
  ctx.putImageData(imageData, 0, 0);
  // Subtract blurred version with slight boost
  ctx.globalCompositeOperation = 'multiply';
  ctx.globalAlpha = 0.15;
  ctx.drawImage(tempCanvas, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
  });
}

/**
 * Detect if image is too blurry for OCR using Laplacian variance
 * Returns { isBlurry: boolean, score: number }
 */
function detectBlur(canvas) {
  const ctx = canvas.getContext('2d');
  const width = Math.min(canvas.width, 640); // Downsample for speed
  const scale = width / canvas.width;
  const height = Math.round(canvas.height * scale);
  
  const smallCanvas = document.createElement('canvas');
  smallCanvas.width = width;
  smallCanvas.height = height;
  const smallCtx = smallCanvas.getContext('2d');
  smallCtx.drawImage(canvas, 0, 0, width, height);
  
  const imageData = smallCtx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  // Convert to grayscale array
  const gray = new Float32Array(width * height);
  for (let i = 0; i < gray.length; i++) {
    gray[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
  }
  
  // Compute Laplacian variance
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const laplacian = -4 * gray[idx] + gray[idx - 1] + gray[idx + 1] + gray[idx - width] + gray[idx + width];
      sum += laplacian;
      sumSq += laplacian * laplacian;
      count++;
    }
  }
  
  const mean = sum / count;
  const variance = (sumSq / count) - (mean * mean);
  
  // Threshold: variance < 100 = blurry, < 50 = very blurry
  return { isBlurry: variance < 100, score: Math.round(variance) };
}

const ReceiptCapture = ({ onReceiptCaptured, onCancel }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isExtraSmall = useMediaQuery('(max-width:480px)');
  const isTinyScreen = useMediaQuery('(max-width:380px)');
  
  const [capturedImage, setCapturedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [captureMode, setCaptureMode] = useState('camera'); // 'camera' or 'file'
  const [waitingForPermission, setWaitingForPermission] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState('environment'); // 'user' for front, 'environment' for back
  const [preprocessingStep, setPreprocessingStep] = useState('');
  const [permissionDenied, setPermissionDenied] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Set up the camera when component mounts or capture mode changes
  useEffect(() => {
    if (captureMode === 'camera') {
      setupCamera();
    }

    // Set proper viewport height for mobile
    const setMobileViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Set initial height
    setMobileViewportHeight();

    // Listen for resize events (orientation changes, keyboard)
    const handleResize = () => {
      setMobileViewportHeight();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Clean up on unmount or when switching modes
    return () => {
      if (streamRef.current) {
        console.log('🔄 Cleaning up camera stream');
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [captureMode, facingMode]);

  const setupCamera = async () => {
    // Stop any existing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    setError(null);
    setWaitingForPermission(true);
    setCameraReady(false);
    
    try {
      console.log('📱 Requesting camera access with facing mode:', facingMode);
      
      const constraints = {
        video: { 
          facingMode: facingMode,
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 960, max: 1080 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      console.log('✅ Camera access granted');
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Wait for video to be ready
        videoRef.current.onloadedmetadata = () => {
          console.log('📹 Video metadata loaded, camera ready');
          setCameraReady(true);
          setWaitingForPermission(false);
        };
      }
    } catch (err) {
      console.error('❌ Error accessing camera:', err);
      
      // More user-friendly error messages based on error types
      if (err.name === 'NotAllowedError') {
        setError(t('receipts.capture.cameraDenied', 'Camera access was denied. You can use the file upload option instead.'));
        setPermissionDenied(true);
        setCaptureMode('file');
      } else if (err.name === 'NotFoundError') {
        setError(t('receipts.capture.noCamera', 'No camera found. You can use the file upload option instead.'));
        setPermissionDenied(true);
        setCaptureMode('file');
      } else if (err.name === 'NotReadableError') {
        setError(t('receipts.capture.cameraInUse', 'Camera is in use by another application. Please close other applications that might be using your camera.'));
      } else {
        setError(`${t('receipts.capture.cameraAccessError', 'Could not access camera')}: ${err.message}`);
      }
      
      setWaitingForPermission(false);
      setCameraReady(false);
    }
  };

  // Flip camera (front/back)
  const flipCamera = () => {
    console.log('🔄 Flipping camera');
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  // Take a picture from the video stream
  const takePicture = async () => {
    if (!videoRef.current || !canvasRef.current) {
      setError(t('receipts.capture.cameraNotInitialized', 'Camera not properly initialized. Please try again or use file upload option.'));
      return;
    }

    if (!cameraReady) {
      setError(t('receipts.capture.cameraNotReady', 'Camera not ready yet. Please wait a moment and try again.'));
      return;
    }
    
    setIsProcessing(true);
    setError(null);
    
    try {
      console.log('📸 Taking picture from camera');
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Check if video has valid dimensions
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        throw new Error('Video not ready - no dimensions available');
      }
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      console.log(`📏 Canvas set to ${canvas.width}x${canvas.height}`);
      
      // Draw the video frame to the canvas
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Check for blur
      const { isBlurry, score } = detectBlur(canvas);
      console.log(`📊 Blur score: ${score} (${isBlurry ? 'BLURRY' : 'OK'})`);

      if (isBlurry) {
        setError(t('receipts.capture.tooBlurry', 'Image is too blurry. Please hold steady and try again.'));
        setIsProcessing(false);
        return;
      }

      // Preprocess image for better OCR
      setPreprocessingStep(t('receipts.capture.enhancing', 'Enhancing image...'));
      const processedBlob = await preprocessImage(canvas);
      setPreprocessingStep('');
      console.log(`✅ Preprocessed image: ${Math.round(processedBlob.size / 1024)} KB`);

      const file = new File([processedBlob], `receipt_${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });

      setCapturedImage(file);
      setIsProcessing(false);
    } catch (err) {
      console.error('❌ Error capturing image:', err);
      setError(`${t('receipts.capture.captureError', 'Failed to capture image')}: ${err.message}`);
      setIsProcessing(false);
    }
  };

  // Handle file selection
  const handleFileChange = async (e) => {
    setError(null);
    
    if (e.target.files && e.target.files[0]) {
      try {
        const file = e.target.files[0];
        console.log(`📁 File selected: ${file.name}, ${Math.round(file.size / 1024)} KB`);
        
        // Basic file validation
        if (file.size > 10 * 1024 * 1024) { // 10MB limit
          setError(t('receipts.capture.fileTooLarge', 'File is too large. Maximum size is 10MB.'));
          return;
        }
        
        if (!file.type.startsWith('image/')) {
          setError(t('receipts.capture.onlyImages', 'Only image files are allowed.'));
          return;
        }
        
        // Preprocess uploaded file for better OCR
        try {
          const img = new Image();
          const url = URL.createObjectURL(file);
          img.src = url;
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          });
          
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = img.naturalWidth;
          tempCanvas.height = img.naturalHeight;
          const tempCtx = tempCanvas.getContext('2d');
          tempCtx.drawImage(img, 0, 0);
          URL.revokeObjectURL(url);
          
          // Check blur
          const { isBlurry, score } = detectBlur(tempCanvas);
          console.log(`📊 Upload blur score: ${score} (${isBlurry ? 'BLURRY' : 'OK'})`);
          
          if (isBlurry) {
            setError(t('receipts.capture.blurWarning', 'Image appears blurry. For best results, use a clearer image.'));
            // Don't block file uploads — just warn (they might be rescanned photos)
          }
          
          setPreprocessingStep(t('receipts.capture.enhancing', 'Enhancing image...'));
          const processedBlob = await preprocessImage(tempCanvas);
          setPreprocessingStep('');
          const processedFile = new File([processedBlob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          console.log(`✅ File preprocessed: ${Math.round(file.size / 1024)}KB → ${Math.round(processedFile.size / 1024)}KB`);
          setCapturedImage(processedFile);
        } catch (preprocessErr) {
          console.warn('⚠️ Preprocessing failed, using original:', preprocessErr.message);
          setCapturedImage(file);
        }
      } catch (err) {
        console.error('❌ Error handling file selection:', err);
        setError(`${t('receipts.capture.fileSelectionError', 'Error selecting file')}: ${err.message}`);
      }
    }
  };

  // Reset captured image
  const resetCapture = () => {
    console.log('🔄 Resetting capture');
    setCapturedImage(null);
    setError(null);
    setIsUploading(false);
    
    if (captureMode === 'file' && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Upload the captured image
  const uploadReceipt = () => {
    if (!capturedImage) {
      setError(t('receipts.capture.noImageCaptured', 'No image captured. Please take or select an image first.'));
      return;
    }
    
    if (isUploading) {
      console.log('⚠️ Upload already in progress, ignoring duplicate request');
      return;
    }
    
    try {
      console.log('🚀 Preparing image for upload');
      setIsUploading(true);
      setError(null);
      
      // Create FormData with the captured image
      const formData = new FormData();
      
      // Generate unique filename
      const filename = `receipt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.jpg`;
      
      // Append the file (works for both File objects and Blobs)
      formData.append('receipt', capturedImage, filename);
      
      // Add metadata
      formData.append('timestamp', new Date().toISOString());
      formData.append('captureMode', captureMode);
      formData.append('fileSize', capturedImage.size.toString());
      
      // Log FormData contents for debugging
      console.log('📦 FormData prepared:');
      for (let [key, value] of formData.entries()) {
        if (value instanceof File || value instanceof Blob) {
          console.log(`  ${key}: ${value.constructor.name}(${value.name || 'blob'}, ${value.size} bytes, ${value.type})`);
        } else {
          console.log(`  ${key}: ${value}`);
        }
      }
      
      // Call parent callback after a small delay to ensure UI updates
      setTimeout(() => {
        console.log('📤 Calling onReceiptCaptured callback');
        onReceiptCaptured(formData);
      }, 100);
      
    } catch (err) {
      console.error('❌ Error preparing upload:', err);
      setError(`${t('receipts.capture.uploadPrepareError', 'Error preparing image for upload')}: ${err.message}`);
      setIsUploading(false);
    }
  };

  // Switch between camera and file upload modes
  const handleCaptureModeChange = (event, newMode) => {
    if (newMode !== null && newMode !== captureMode) {
      // R14: Confirm before losing captured image
      if (capturedImage) {
        if (!window.confirm(t('receipts.capture.discardConfirm', 'Switching modes will discard your captured image. Continue?'))) {
          return;
        }
        setCapturedImage(null);
      }
      console.log('🔄 Switching capture mode to:', newMode);
      setCaptureMode(newMode);
      setError(null);
      setIsUploading(false);
    }
  };

  return (
    <Fade in timeout={600}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: isMobile ? 'calc(var(--vh, 1vh) * 100)' : '100vh',
          bgcolor: isMobile 
            ? theme.palette.background.default
            : alpha(theme.palette.common.black, 0.8),
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Enhanced Mobile-First Header */}
        <Paper
          elevation={0}
          sx={{
            background: isMobile
              ? theme.palette.mode === 'dark' 
                ? `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`
                : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
              : theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderRadius: 0,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ 
              p: isTinyScreen ? 1.5 : { xs: 2, sm: 2.5 }, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: isTinyScreen ? 1 : 1.5 }}>
                <Box
                  sx={{
                    position: 'relative',
                    bgcolor: alpha(theme.palette.common.white, 0.15),
                    borderRadius: '50%',
                    p: isTinyScreen ? 0.75 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ScannerIcon fontSize={isTinyScreen ? "small" : "medium"} />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -2,
                      right: -2,
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.common.white, 0.8),
                      animation: 'scan-pulse 2s infinite',
                      '@keyframes scan-pulse': {
                        '0%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.5, transform: 'scale(1.3)' },
                        '100%': { opacity: 1, transform: 'scale(1)' }
                      }
                    }}
                  />
                </Box>
                <Typography 
                  variant={isTinyScreen ? "subtitle1" : "h6"}
                  sx={{ 
                    fontWeight: 700,
                    fontSize: isTinyScreen ? '1rem' : undefined
                  }}
                >
                  {isTinyScreen 
                    ? (captureMode === 'camera' ? t('receipts.capture.scan', 'Scan') : t('receipts.capture.upload', 'Upload'))
                    : (captureMode === 'camera' ? t('receipts.capture.scanReceipt', 'Scan Receipt') : t('receipts.capture.uploadReceipt', 'Upload Receipt'))
                  }
                </Typography>
              </Box>
              <IconButton 
                color="inherit" 
                onClick={onCancel}
                aria-label="Close camera"
                size={isTinyScreen ? "small" : "medium"}
                sx={{
                  bgcolor: alpha(theme.palette.common.white, 0.1),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.2),
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <CloseIcon fontSize={isTinyScreen ? "small" : "medium"} />
              </IconButton>
            </Box>
            
            {/* Enhanced Mobile-First Tabs */}
            <Tabs
              value={captureMode}
              onChange={handleCaptureModeChange}
              variant="fullWidth"
              sx={{ 
                borderTop: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  background: alpha(theme.palette.common.white, 0.9)
                },
                '& .MuiTab-root': {
                  color: alpha(theme.palette.common.white, 0.7),
                  fontWeight: 600,
                  fontSize: isTinyScreen ? '0.75rem' : { xs: '0.8rem', sm: '0.9rem' },
                  textTransform: 'none',
                  minHeight: isTinyScreen ? 48 : { xs: 56, sm: 64 },
                  transition: 'all 0.3s ease',
                  '&.Mui-selected': {
                    color: 'common.white',
                    fontWeight: 700,
                  },
                  '&:hover': {
                    color: 'common.white',
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                  }
                }
              }}
            >
              <Tab 
                icon={<CameraEnhanceIcon fontSize={isTinyScreen ? "small" : "medium"} />} 
                label={t('receipts.capture.camera', 'Camera')} 
                value="camera"
                iconPosition="start"
                sx={{ 
                  '& .MuiSvgIcon-root': { 
                    mr: isTinyScreen ? 0.5 : 1
                  } 
                }}
              />
              <Tab 
                icon={<UploadIcon fontSize={isTinyScreen ? "small" : "medium"} />} 
                label={t('receipts.capture.upload', 'Upload')} 
                value="file"
                iconPosition="start"
                sx={{ 
                  '& .MuiSvgIcon-root': { 
                    mr: isTinyScreen ? 0.5 : 1
                  } 
                }}
              />
            </Tabs>
          </Box>
        </Paper>
        
        {/* Enhanced Mobile-First Error Display */}
        {error && (
          <Slide direction="down" in={Boolean(error)} mountOnEnter unmountOnExit>
            <Alert 
              severity="error" 
              sx={{ 
                m: isTinyScreen ? 1 : { xs: 1.5, sm: 2 },
                borderRadius: 2,
                fontWeight: 500,
                fontSize: isTinyScreen ? '0.8rem' : undefined
              }}
            >
              {error}
            </Alert>
          </Slide>
        )}
        
        {/* R11: Camera permission denied banner */}
        {permissionDenied && (
          <Alert 
            severity="warning" 
            sx={{ m: 2, borderRadius: 2 }}
            action={
              <Button size="small" onClick={() => setPermissionDenied(false)}>
                {t('common.dismiss', 'Dismiss')}
              </Button>
            }
          >
            {t('receipts.capture.permissionDeniedBanner', 'Camera access was denied. You can still upload receipt photos from your gallery. To enable camera, check your browser\'s site permissions.')}
          </Alert>
        )}
        
        {/* Main Content Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {captureMode === 'camera' ? (
            // Enhanced Mobile-First Camera capture UI
            <Box sx={{ 
              position: 'relative', 
              width: '100%', 
              flex: 1,
              bgcolor: 'common.black',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {!capturedImage ? (
                // Camera view with centered capture button
                <>
                  {/* Video Container */}
                  <Box sx={{ 
                    position: 'relative',
                    flex: 1,
                    overflow: 'hidden',
                    width: '100%',
                    height: '100%'
                  }}>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                    
                    {/* Enhanced Mobile-First Waiting for permission overlay */}
                    {waitingForPermission && (
                      <Box sx={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: alpha(theme.palette.common.black, 0.8),
                        zIndex: 2,
                        padding: isTinyScreen ? 2 : 3
                      }}>
                        <Box
                          sx={{
                            position: 'relative',
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            borderRadius: '50%',
                            p: 3,
                            mb: 3,
                            border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`
                          }}
                        >
                          <CircularProgress 
                            color="primary" 
                            size={isTinyScreen ? 40 : 48} 
                            sx={{ 
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)'
                            }}
                          />
                          <CameraIcon sx={{ fontSize: isTinyScreen ? 24 : 32, color: 'common.white' }} />
                        </Box>
                        <Typography 
                          variant={isTinyScreen ? "subtitle1" : "h6"} 
                          sx={{ 
                            color: 'common.white',
                            fontWeight: 600,
                            textAlign: 'center',
                            mb: 1
                          }}
                        >
                          {isTinyScreen ? t('receipts.capture.cameraAccessShort', 'Camera access...') : t('receipts.capture.waitingForPermission', 'Waiting for camera permission...')}
                        </Typography>
                        <Typography 
                          variant={isTinyScreen ? "caption" : "body2"} 
                          sx={{ 
                            color: 'common.white',
                            textAlign: 'center',
                            opacity: 0.8,
                            fontSize: isTinyScreen ? '0.75rem' : undefined
                          }}
                        >
                          {isTinyScreen 
                            ? t('receipts.capture.allowCamera', 'Please allow camera access')
                            : t('receipts.capture.allowCameraDialog', 'Please click "Allow" in your browser\'s permission dialog')
                          }
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Enhanced Mobile-First Camera controls and guidance */}
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      right: 0, 
                      bottom: 0, 
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: isTinyScreen ? 1 : 2,
                      pointerEvents: 'none' // Allow clicks to pass through
                    }}>
                      {/* Top controls */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        pointerEvents: 'auto' // Re-enable clicks for controls
                      }}>
                        <Box sx={{ 
                          bgcolor: alpha(theme.palette.common.black, 0.6), 
                          p: isTinyScreen ? 0.75 : 1, 
                          borderRadius: 2,
                          backdropFilter: 'blur(8px)',
                          border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`
                        }}>
                          <Typography 
                            variant={isTinyScreen ? "caption" : "body2"} 
                            sx={{ 
                              color: 'common.white',
                              fontWeight: 500,
                              fontSize: isTinyScreen ? '0.7rem' : undefined
                            }}
                          >
                            {isTinyScreen ? t('receipts.capture.positionReceiptShort', 'Position receipt in frame') : t('receipts.capture.positionReceipt', 'Position receipt within frame')}
                          </Typography>
                        </Box>
                        
                        {/* Enhanced Flip camera button */}
                        <IconButton
                          onClick={flipCamera}
                          aria-label="Switch camera"
                          disabled={!cameraReady || waitingForPermission}
                          size={isTinyScreen ? "small" : "medium"}
                          sx={{
                            backgroundColor: alpha(theme.palette.common.black, 0.6),
                            color: 'common.white',
                            backdropFilter: 'blur(8px)',
                            border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': { 
                              backgroundColor: alpha(theme.palette.common.black, 0.8),
                              transform: 'scale(1.05)'
                            },
                            '&:disabled': {
                              backgroundColor: alpha(theme.palette.common.black, 0.3),
                              color: alpha(theme.palette.common.white, 0.5)
                            }
                          }}
                        >
                          <FlipCameraIcon fontSize={isTinyScreen ? "small" : "medium"} />
                        </IconButton>
                      </Box>
                      
                      {/* Enhanced Mobile-First Receipt frame guide */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: isTinyScreen ? '15%' : '20%',
                          left: isTinyScreen ? '5%' : '10%',
                          right: isTinyScreen ? '5%' : '10%',
                          bottom: isTinyScreen ? '15%' : '20%',
                          border: `2px dashed ${alpha(theme.palette.common.white, 0.8)}`,
                          borderRadius: 3,
                          pointerEvents: 'none',
                          background: `linear-gradient(45deg, transparent 40%, ${alpha(theme.palette.common.white, 0.05)} 50%, transparent 60%)`,
                          animation: 'scan-line 3s infinite',
                          '@keyframes scan-line': {
                            '0%': { backgroundPosition: '-100% 0' },
                            '100%': { backgroundPosition: '100% 0' }
                          }
                        }}
                      >
                        {/* Corner indicators */}
                        {[
                          { top: -2, left: -2 },
                          { top: -2, right: -2 },
                          { bottom: -2, left: -2 },
                          { bottom: -2, right: -2 }
                        ].map((corner, index) => (
                          <Box
                            key={index}
                            aria-hidden="true"
                            sx={{
                              position: 'absolute',
                              width: isTinyScreen ? 16 : 20,
                              height: isTinyScreen ? 16 : 20,
                              border: `3px solid ${theme.palette.common.white}`,
                              borderRadius: 1,
                              ...corner
                            }}
                          />
                        ))}
                        {/* R12: Frame alignment hint text */}
                        <Typography variant="caption" sx={{
                          position: 'absolute', bottom: '15%', left: '50%', transform: 'translateX(-50%)',
                          color: alpha(theme.palette.common.white, 0.7), textAlign: 'center', textShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.5)}`,
                          pointerEvents: 'none', whiteSpace: 'nowrap'
                        }}>
                          {t('receipts.capture.alignHint', 'Align receipt within frame')}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Centered Capture Button with Transparency */}
                    <Box sx={{ 
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={isProcessing || waitingForPermission || !cameraReady}
                        onClick={takePicture}
                        sx={{ 
                          width: isTinyScreen ? 72 : 84, 
                          height: isTinyScreen ? 72 : 84, 
                          borderRadius: '50%',
                          minWidth: 'auto',
                          backgroundColor: alpha(theme.palette.common.white, 0.85),
                          color: theme.palette.primary.main,
                          boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}`,
                          border: `4px solid ${alpha(theme.palette.common.white, 0.9)}`,
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.common.white, 0.95),
                            transform: 'translate(-50%, -50%) scale(1.1)',
                            boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.5)}`
                          },
                          '&:active': {
                            transform: 'translate(-50%, -50%) scale(0.95)'
                          },
                          '&:disabled': {
                            backgroundColor: alpha(theme.palette.common.white, 0.4),
                            color: alpha(theme.palette.common.black, 0.3),
                            border: `4px solid ${alpha(theme.palette.common.white, 0.3)}`
                          }
                        }}
                      >
                        {isProcessing ? (
                          <CircularProgress size={isTinyScreen ? 28 : 36} color="inherit" />
                        ) : (
                          <CameraIcon fontSize={isTinyScreen ? "large" : "large"} />
                        )}
                      </Button>
                      {preprocessingStep && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mt: 2,
                            color: 'common.white',
                            backgroundColor: alpha(theme.palette.common.black, 0.6),
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                            fontWeight: 500,
                            textAlign: 'center'
                          }}
                        >
                          {preprocessingStep}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </>
              ) : (
                // Enhanced Mobile-First Captured image preview
                <Box sx={{ 
                  position: 'relative', 
                  width: '100%', 
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'common.black'
                }}>
                  <Box 
                    component="img"
                    src={URL.createObjectURL(capturedImage)}
                    alt={t('receipts.capture.capturedReceiptAlt', 'Captured receipt')}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                  
                  {/* Enhanced Success overlay */}
                  <Box sx={{
                    position: 'absolute',
                    top: isTinyScreen ? 12 : 16,
                    left: isTinyScreen ? 12 : 16,
                    backgroundColor: alpha(theme.palette.success.main, 0.95),
                    color: 'common.white',
                    px: isTinyScreen ? 1.5 : 2,
                    py: isTinyScreen ? 0.75 : 1,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`,
                    backdropFilter: 'blur(8px)'
                  }}>
                    <CheckIcon fontSize={isTinyScreen ? "small" : "medium"} />
                    <Typography 
                      variant={isTinyScreen ? "caption" : "body2"} 
                      fontWeight={600}
                      sx={{ fontSize: isTinyScreen ? '0.75rem' : undefined }}
                    >
                      {isTinyScreen ? t('receipts.capture.captured', 'Captured!') : t('receipts.capture.photoCapturedSuccess', 'Photo captured successfully')}
                    </Typography>
                  </Box>
                </Box>
              )}
              
              {/* Hidden canvas used for capture */}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </Box>
          ) : (
            // Enhanced Mobile-First File upload UI
            <Box sx={{ 
              flex: 1,
              p: isTinyScreen ? 2 : { xs: 3, sm: 4 }, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: capturedImage ? 'flex-start' : 'center',
              bgcolor: theme.palette.background.default
            }}>
              {!capturedImage ? (
                // Enhanced File selection UI
                <Box sx={{ 
                  textAlign: 'center',
                  maxWidth: 400,
                  width: '100%'
                }}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: isTinyScreen ? 80 : 120,
                      height: isTinyScreen ? 80 : 120,
                      mx: 'auto',
                      mb: 3,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`
                    }}
                  >
                    <UploadIcon 
                      sx={{ 
                        fontSize: isTinyScreen ? 32 : 48, 
                        color: theme.palette.primary.main 
                      }} 
                    />
                  </Box>
                  
                  <input
                    accept="image/*"
                    id="receipt-file-input"
                    type="file"
                    hidden
                    ref={fileInputRef}
                    onChange={handleFileChange}
                  />
                  <label htmlFor="receipt-file-input">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<UploadIcon />}
                      size={isTinyScreen ? "medium" : "large"}
                      sx={{ 
                        mb: 2,
                        borderRadius: 3,
                        px: isTinyScreen ? 3 : 4,
                        py: isTinyScreen ? 1 : 1.5,
                        fontSize: isTinyScreen ? '0.9rem' : '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.4)}`
                        }
                      }}
                    >
                      {t('receipts.capture.selectImage', 'Select Image')}
                    </Button>
                  </label>
                  
                  <Typography 
                    variant={isTinyScreen ? "body2" : "body1"} 
                    color="text.primary" 
                    sx={{ 
                      fontWeight: 500,
                      mb: 1,
                      fontSize: isTinyScreen ? '0.9rem' : undefined
                    }}
                  >
                    {isTinyScreen ? t('receipts.capture.chooseImageShort', 'Choose a clear receipt image') : t('receipts.capture.chooseImage', 'Select a clear image of your receipt')}
                  </Typography>
                  
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      fontSize: isTinyScreen ? '0.7rem' : undefined,
                      opacity: 0.8
                    }}
                  >
                    {isTinyScreen ? t('receipts.capture.formatsShort', 'JPG, PNG, HEIC (Max: 10MB)') : t('receipts.capture.formats', 'Supported formats: JPG, PNG, HEIC (Max: 10MB)')}
                  </Typography>
                </Box>
              ) : (
                // Enhanced Selected file preview
                <Box sx={{ width: '100%', textAlign: 'center' }}>
                  <Box 
                    component="img"
                    src={URL.createObjectURL(capturedImage)}
                    alt={t('receipts.capture.selectedReceiptAlt', 'Selected receipt')}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: isTinyScreen ? 250 : 300,
                      objectFit: 'contain',
                      mb: 2,
                      borderRadius: 2,
                      boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`
                    }}
                  />
                  <Typography 
                    variant={isTinyScreen ? "caption" : "body2"} 
                    color="text.secondary"
                    sx={{ fontSize: isTinyScreen ? '0.75rem' : undefined }}
                  >
                    {capturedImage.name || t('receipts.capture.selectedImage', 'Selected image')} ({Math.round(capturedImage.size / 1024)} KB)
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
        
        {/* Enhanced Mobile-First Action Buttons */}
        {capturedImage && (
          <Slide direction="up" in={Boolean(capturedImage)} mountOnEnter>
            <Paper
              elevation={0}
              sx={{ 
                p: isTinyScreen ? 1.5 : 2, 
                display: 'flex', 
                justifyContent: 'space-between',
                gap: isTinyScreen ? 1 : 2,
                bgcolor: theme.palette.background.paper,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
              }}
            >
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={resetCapture}
                disabled={isUploading}
                size={isTinyScreen ? "medium" : "large"}
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: isTinyScreen ? '0.8rem' : undefined,
                  py: isTinyScreen ? 1 : 1.5,
                  borderColor: alpha(theme.palette.text.primary, 0.3),
                  color: theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme.palette.text.primary,
                    bgcolor: alpha(theme.palette.text.primary, 0.04)
                  }
                }}
              >
                {captureMode === 'camera' ? t('receipts.capture.retake', 'Retake') : t('receipts.capture.change', 'Change')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                onClick={uploadReceipt}
                disabled={isUploading}
                size={isTinyScreen ? "medium" : "large"}
                sx={{
                  flex: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: isTinyScreen ? '0.8rem' : undefined,
                  py: isTinyScreen ? 1 : 1.5,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                {isUploading 
                  ? t('receipts.capture.processing', 'Processing...') 
                  : isTinyScreen 
                    ? t('receipts.capture.use', 'Use') 
                    : `${t('receipts.capture.usePrefix', 'Use')} ${captureMode === 'camera' ? t('receipts.capture.photo', 'Photo') : t('receipts.capture.image', 'Image')}`
                }
              </Button>
            </Paper>
          </Slide>
        )}
        
        {/* Enhanced Mobile-First Help Text */}
        <Paper
          elevation={0}
          sx={{ 
            p: isTinyScreen ? 1.5 : 2, 
            bgcolor: alpha(theme.palette.background.default, 0.6),
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <Typography 
            variant={isTinyScreen ? "caption" : "body2"} 
            color="text.secondary"
            sx={{ 
              textAlign: 'center',
              fontSize: isTinyScreen ? '0.7rem' : undefined,
              lineHeight: 1.4
            }}
          >
            {captureMode === 'camera' 
              ? isTinyScreen
                ? t('receipts.capture.lightingTipShort', 'Ensure good lighting and clear text visibility')
                : t('receipts.capture.lightingTip', 'Ensure good lighting and position the receipt so all text is clearly visible.')
              : isTinyScreen
                ? t('receipts.capture.uploadTipShort', 'Upload a clear, well-lit receipt image')
                : t('receipts.capture.uploadTip', 'For best results, upload a clear, well-lit image of the entire receipt.')
            }
          </Typography>
        </Paper>
      </Box>
    </Fade>
  );
};

export default ReceiptCapture;